import React, { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import MapPreview from "../../map/mapPreview";
import ResponsiveGrid from "../../grid/responsiveGrid";

import { CustomPagination } from "../../customPagination";
import { ConvertMetersToFeet } from "../../util";
import { useUserAuth } from "../../contexts/authContext";
import { useMap } from "../../contexts/mapContext";
import { useNavigate } from "react-router";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { canCreateOrDeleteCoaVolumes } from "../operations/opUtil";

export default function COAVolumes() {
    const [showMap, setShowMap] = useState(false);
    const [selectedCoaVolumes, setSelectedCoaVolumes] = useState([]);
    const [isSelected, setIsSelected] = useState(false);

    const navigate = useNavigate();
    const { user, getOrganizationByID, handleFailedFetch, setSnackbar } = useUserAuth();
    const { coaVolumes } = useMap();

    const columns = [
        {
            field: "name",
            headerName: "Name",
            editable: false,
            minWidth: 150,
            flex: 1
        },
        {
            field: "altitude_agl_m",
            headerName: "Alt ft (AGL)",
            editable: false,
            minWidth: 125,
            flex: 1,
            valueGetter: (value) => `${ConvertMetersToFeet(value)}`
        },
        {
            field: "organization_id",
            headerName: "Org",
            editable: false,
            minWidth: 75,
            flex: 1,
            renderCell: (cellValues) => {
                const org = getOrganizationByID(cellValues.formattedValue);
                if (org != undefined) {
                    return <div>{org.name}</div>;
                }
            }
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            getActions: (params) => {
                if (canCreateOrDeleteCoaVolumes(user, params.row)) {
                    return [
                        <GridActionsCellItem
                            key={params.id}
                            icon={<DeleteIcon />}
                            label="Delete"
                            className="textPrimary"
                            onClick={() => handleDeleteClick(params.row)}
                            color="inherit"
                            id={`delete-${params.row.volumeId}`}
                        />
                    ];
                }
                return [];
            }
        }
    ];

    useEffect(() => {
        if (!isSelected) {
            setSelectedCoaVolumes(coaVolumes);
        }
    }, [coaVolumes, selectedCoaVolumes, isSelected]);

    const handleDeleteClick = async (row) => {
        if (!confirm("Are you sure you want to delete this COA Volume?")) {
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        await fetch(`/api/coaVolume/delete/${row.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: "COA Volume Successfully Deleted", severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleCheckboxSelection = (ids) => {
        if (ids.length > 0) {
            setShowMap(true);
        }

        const selected = coaVolumes.filter(({ id }) => ids.includes(id));
        if (selected.length > 0) {
            setSelectedCoaVolumes(selected);
            setIsSelected(true);
        } else {
            setSelectedCoaVolumes(coaVolumes);
            setIsSelected(false);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "column" },
                width: "100%",
                height: "100%",
                justifyContent: { xs: "flex-end", sm: "flex-start" }
            }}
        >
            <Paper
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "column" },
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1, sm: 2 },
                    borderTopRightRadius: 0,
                    botderTopLeftRadius: 0,
                    height: { xs: "100%", sm: "578px" },
                    flexShrink: { xs: "unset", sm: 0 }
                }}
            >
                <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <ResponsiveGrid
                        rows={coaVolumes}
                        columns={columns}
                        checkboxSelection
                        disableVirtualization
                        disableRowSelectionOnClick
                        pagination
                        slots={{ pagination: CustomPagination }}
                        onRowSelectionModelChange={handleCheckboxSelection}
                        sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        data-testid="largePreview"
                        onClick={() => setShowMap((prev) => !prev)}
                        sx={{ mr: "auto", whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                        {showMap ? "Hide Map" : "Preview on Map"}
                    </Button>
                    {canCreateOrDeleteCoaVolumes(user) && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            data-testid="create"
                            id="createCoaVolume"
                            onClick={() => navigate("/coa")}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                        >
                            Create COA Volume
                        </Button>
                    )}
                </Box>
            </Paper>

            {showMap && (
                <Paper sx={{ flexShrink: 0, height: { xs: "40%", sm: "400px" }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 0 }, pb: { xs: 0, sm: 2 } }}>
                    <MapPreview selected_coa_volumes={selectedCoaVolumes} />
                </Paper>
            )}
        </Box>
    );
}
